<template>
  <ul role="menu">
    <li role="none">
      <button
        class="-outline-offset-1 flex items-center py-4 fluid-sm tracking-wider uppercase font-medium border-b border-br-green w-full px-6 relative"
        @click="closeMenu"
        :aria-label="$t('navigation.backToPreviousMenu')"
        role="menuitem"
      >
        <div class="absolute left-0 inset-y-0 flex items-center">
          <AngleLeftIcon class="h-5 fill-current inline-block" aria-hidden="true" />
        </div>
        {{ navigationTitle }}
      </button>
    </li>
    <li v-if="navItem.link" role="none">
      <MobileNavLink v-bind="{ link: navItem.link }" class="pl-6" role="menuitem" />
    </li>

    <template v-if="childElements">
      <li v-for="(item, indexA) in childElements" :key="indexA" role="none">
        <div v-if="item.links || item.link">
          <MobileNavButton
            v-if="item.links && item.links.length > 1"
            v-bind="{ ...item.links[0] }"
            :on-click="() => handleNavButtonClick(indexA)"
            :is-open="openIndex !== indexA"
            :is-new="item.links[0].new"
            :tabindex="openIndex > 1 ? '-1' : '0'"
            role="menuitem"
            :aria-expanded="openIndex === indexA ? 'true' : 'false'"
            aria-haspopup="true"
          />

          <MobileNavLink
            v-else
            v-bind="item.link ? item : item.links ? item.links[0] : {}"
            :is-new="item.link ? item.link.new : item.links ? item.links[0].new : false"
            class="pl-6"
            :tabindex="openIndex > 1 ? '-1' : '0'"
            role="menuitem"
          />

          <transition
            enter-class="translate-x-full opacity-0"
            enter-to-class="translate-x-0 opacity-100"
            enter-active-class="transition duration-200 ease-out"
            leave-class="translate-x-0 opacity-100"
            leave-to-class="translate-x-full opacity-0"
            leave-active-class="transition duration-200 ease-out"
            @after-enter="onTransitionComplete"
          >
            <ChildNavigation
              v-if="openIndex === indexA"
              class="fixed w-80 left-0 right-0 bottom-0 top-12 z-10 bg-white"
              v-bind="{ navItem: item }"
              @closeMenu="handleCloseChildMenu"
            />
          </transition>
        </div>
      </li>
    </template>
  </ul>
</template>

<script setup>
import MobileNavLink from '~/components/layout/header/mobileDrawer/MobileNavLink';
import MobileNavButton from '~/components/layout/header/mobileDrawer/MobileNavButton';
import AngleLeftIcon from '~/components/icons/AngleLeftIcon.vue';

const props = defineProps({
  navItem: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['closeMenu']);
const openIndex = ref(-1);
const lastFocusedElement = ref(null);

const firstLink = computed(() => {
  return props.navItem.link ?? props.navItem.links?.[0];
});

const navigationTitle = computed(() => {
  return props.navItem.title ?? firstLink.value?.mobileTitle;
});

const childElements = computed(() => {
  if (props.navItem.navBlocks && props.navItem.navBlocks.length > 0) {
    return props.navItem.navBlocks;
  }

  if (props.navItem.links && props.navItem.links.length > 0) {
    return props.navItem.links;
  }

  return [];
});

const closeMenu = () => {
  emit('closeMenu');
};

const handleNavButtonClick = (indexA) => {
  // Store reference to the currently focused element before opening submenu
  lastFocusedElement.value = document.activeElement;

  if (openIndex.value === indexA) {
    openIndex.value = -1;
  } else {
    openIndex.value = indexA;
  }
};

const handleCloseChildMenu = () => {
  openIndex.value = -1;

  // Return focus to the element that opened the submenu
  if (lastFocusedElement.value) {
    setTimeout(() => {
      lastFocusedElement.value.focus();
    }, 0);
  }
};

const onTransitionComplete = () => {
  // Find first focusable element in submenu and focus it
  const submenu = document.querySelector('.fixed.w-80');
  if (submenu) {
    const firstFocusable = submenu.querySelector('button, a, [tabindex="0"]');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }
};

// Handle escape key to close menus
const handleKeyDown = (e) => {
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    if (openIndex.value !== -1) {
      handleCloseChildMenu();
    } else {
      closeMenu();
    }
  }
};

// Set up and clean up keyboard event listeners
onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown);
});
</script>
